import { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Upload, Plus, Search, Trash2, Users, X, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle } from 'lucide-react';

const STATUS_COLORS = {
  new:'badge-gray',
  emailed:'badge-purple',
  replied:'badge-green',
  bounced:'badge-red',
  unsubscribed:'badge-amber'
};

const scoreColor = s => s >= 70 ? 'var(--green)' : s >= 45 ? 'var(--amber)' : 'var(--red)';
const scoreBg = s => s >= 70 ? 'rgba(22,163,74,.1)' : s >= 45 ? 'rgba(217,119,6,.1)' : 'rgba(220,38,38,.1)';

const RISKY_DOMAINS = ['yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com'];

const validateCSVBeforeUpload = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      const warnings = [];
      const errors = [];
      const emails = [];
      const invalidEmails = [];
      const duplicateEmails = [];
      const riskyEmails = [];

      if (lines.length < 2) {
        errors.push('CSV appears empty or has no data rows.');
        return resolve({ valid: false, errors, warnings, count: 0 });
      }

      const headers = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/"/g, ''));
      const hasEmail = headers.some(h => h.includes('email'));
      const hasName = headers.some(h => h.includes('name'));

      if (!hasEmail) errors.push('No "email" column found. Make sure first row has headers: name, email, company, role');
      if (!hasName) errors.push('No "name" column found. Make sure first row has headers: name, email, company, role');

      if (errors.length > 0) return resolve({ valid: false, errors, warnings, count: 0 });

      const emailIndex = headers.findIndex(h => h.includes('email'));

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map(c => c.trim().replace(/"/g, ''));
        const email = cols[emailIndex]?.toLowerCase();
        if (!email) continue;

        if (!email.includes('@') || !email.includes('.')) {
          invalidEmails.push(email);
        } else {
          const domain = email.split('@')[1];
          if (RISKY_DOMAINS.includes(domain)) riskyEmails.push(email);
          if (emails.includes(email)) duplicateEmails.push(email);
          emails.push(email);
        }
      }

      if (invalidEmails.length > 0) warnings.push({
        message: `${invalidEmails.length} email${invalidEmails.length > 1 ? 's' : ''} look invalid — missing @ or domain`,
        emails: invalidEmails
      });
      if (duplicateEmails.length > 0) warnings.push({
        message: `${duplicateEmails.length} duplicate email${duplicateEmails.length > 1 ? 's' : ''} found — only first will be imported`,
        emails: duplicateEmails
      });
      if (riskyEmails.length > 0) warnings.push({
        message: `${riskyEmails.length} personal email${riskyEmails.length > 1 ? 's' : ''} (Gmail/Yahoo) — business emails get better delivery`,
        emails: riskyEmails
      });

      resolve({ valid: true, errors, warnings, count: emails.length });
    };
    reader.readAsText(file);
  });
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const fileRef = useRef();

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/leads?page=${page}&limit=20`);
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load leads.'); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLeads(); }, [page]);

  const handleCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = await validateCSVBeforeUpload(file);

    if (!validation.valid) {
      validation.errors.forEach(err => toast.error(err, { duration: 6000 }));
      e.target.value = '';
      return;
    }

    if (validation.warnings.length > 0) {
      setPendingFile(file);
      setCsvPreview(validation);
      e.target.value = '';
      return;
    }

    await uploadFile(file);
    e.target.value = '';
  };

  const uploadFile = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('csv', file);
    try {
      const res = await api.post('/leads/upload-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${res.data.inserted} leads!`);
      if (res.data.errors?.length > 0) {
        toast.error(`${res.data.errors.length} rows skipped`, { duration: 4000 });
      }
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.error || 'CSV upload failed.'); }
    finally { setUploading(false); setCsvPreview(null); setPendingFile(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads(l => l.filter(x => x.id !== id));
      toast.success('Lead deleted.');
    } catch { toast.error('Failed to delete.'); }
  };

  const filtered = search.trim()
    ? leads.filter(l => `${l.name} ${l.email} ${l.company} ${l.role}`.toLowerCase().includes(search.toLowerCase()))
    : leads;

  return (
    <div style={{ padding:32, maxWidth:1100 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, letterSpacing:'-.02em' }}>Leads</h1>
          <p style={{ color:'var(--muted)', fontSize:13, marginTop:4 }}>
            {total} contact{total !== 1 ? 's' : ''} in your database
          </p>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <input ref={fileRef} type="file" accept=".csv" onChange={handleCSV} style={{ display:'none' }} />
          <button className="btn btn-secondary" onClick={() => fileRef.current?.click()} disabled={uploading}>
            <Upload size={14} />{uploading ? 'Importing...' : 'Import CSV'}
          </button>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
            <Plus size={14} /> Add Lead
          </button>
        </div>
      </div>

      {/* CSV VALIDATION MODAL */}
      {csvPreview && (
        <div className="modal-overlay" onClick={() => { setCsvPreview(null); setPendingFile(null); }}>
          <div className="modal" style={{ maxWidth:480 }} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h2 style={{ fontSize:16, fontWeight:700 }}>CSV Preview</h2>
              <button className="btn btn-ghost" onClick={() => { setCsvPreview(null); setPendingFile(null); }}>
                <X size={16} />
              </button>
            </div>

            <div style={{
              background:'rgba(22,163,74,.06)',
              border:'1px solid rgba(22,163,74,.2)',
              borderRadius:9, padding:'12px 14px', marginBottom:14,
              display:'flex', alignItems:'center', gap:10
            }}>
              <CheckCircle size={16} color="var(--green)" />
              <p style={{ fontSize:13, color:'var(--text)', fontWeight:500 }}>
                {csvPreview.count} valid contacts found
              </p>
            </div>

            {csvPreview.warnings.length > 0 && (
              <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
                <p style={{ fontSize:12, fontWeight:600, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.04em' }}>
                  Warnings
                </p>
                {csvPreview.warnings.map((w, i) => (
                  <div key={i} style={{
                    background:'rgba(217,119,6,.06)',
                    border:'1px solid rgba(217,119,6,.2)',
                    borderRadius:8, padding:'10px 12px',
                    display:'flex', flexDirection:'column', gap:6
                  }}>
                    <div style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
                      <AlertTriangle size={14} color="var(--amber)" style={{ flexShrink:0, marginTop:1 }} />
                      <p style={{ fontSize:13, color:'var(--text)', fontWeight:500 }}>{w.message}</p>
                    </div>
                    {w.emails?.length > 0 && (
                      <div style={{ paddingLeft:22, display:'flex', flexWrap:'wrap', gap:4 }}>
                        {w.emails.map(email => (
                          <span key={email} style={{
                            fontSize:11, background:'rgba(217,119,6,.1)',
                            color:'var(--amber)', padding:'1px 7px',
                            borderRadius:4, fontFamily:'monospace'
                          }}>
                            {email}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <p style={{ fontSize:12, color:'var(--muted)', marginBottom:16 }}>
              You can still import — these are warnings not errors. We recommend cleaning your list first for better delivery rates.
            </p>

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setCsvPreview(null); setPendingFile(null); }}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={() => uploadFile(pendingFile)} disabled={uploading}>
                {uploading ? 'Importing...' : `Import anyway (${csvPreview.count} contacts)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV FORMAT HINT */}
      <div style={{
        background:'rgba(108,71,255,.06)',
        border:'1px solid rgba(108,71,255,.15)',
        borderRadius:9, padding:'10px 14px',
        marginBottom:16, display:'flex',
        alignItems:'center', gap:10
      }}>
        <span style={{ fontSize:16 }}>💡</span>
        <p style={{ fontSize:12, color:'var(--muted)' }}>
          CSV format: <code style={{ background:'var(--bg3)', padding:'1px 6px', borderRadius:4, fontSize:11 }}>name, email, company, role</code> — first row must be headers
        </p>
      </div>

      {/* SEARCH */}
      <div style={{ position:'relative', marginBottom:16 }}>
        <Search size={14} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--muted)' }} />
        <input
          className="input"
          placeholder="Search by name, email, company, or role..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ paddingLeft:36 }}
        />
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:48 }}>
          <div className="spinner" style={{ margin:'0 auto' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          border:'2px dashed var(--border)', borderRadius:16,
          padding:'64px 32px', textAlign:'center'
        }}>
          <div style={{
            width:56, height:56, borderRadius:14,
            background:'var(--glow)', display:'flex',
            alignItems:'center', justifyContent:'center',
            margin:'0 auto 16px'
          }}>
            <Users size={24} color="var(--accent)" />
          </div>
          <p style={{ fontWeight:700, fontSize:16, marginBottom:6 }}>No leads yet</p>
          <p style={{ color:'var(--muted)', fontSize:13, marginBottom:20 }}>
            Import a CSV file or add leads manually to get started
          </p>
          <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
            <button className="btn btn-secondary" onClick={() => fileRef.current?.click()}>
              <Upload size={13} /> Import CSV
            </button>
            <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
              <Plus size={13} /> Add Lead
            </button>
          </div>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Company</th>
                <th>Role</th>
                <th>Score</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(lead => (
                <tr key={lead.id}>
                  <td><div style={{ fontWeight:600, fontSize:13 }}>{lead.name}</div></td>
                  <td style={{ color:'var(--muted)', fontSize:12 }}>{lead.email}</td>
                  <td style={{ color:'var(--text)', fontSize:13 }}>{lead.company || '—'}</td>
                  <td style={{ color:'var(--muted)', fontSize:12 }}>{lead.role || '—'}</td>
                  <td>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ width:56, height:6, background:'var(--border)', borderRadius:3, overflow:'hidden' }}>
                        <div style={{ width:`${lead.score || 0}%`, height:'100%', background:scoreColor(lead.score || 0), borderRadius:3, transition:'width .3s' }}/>
                      </div>
                      <span style={{ fontSize:12, fontWeight:700, color:scoreColor(lead.score || 0), background:scoreBg(lead.score || 0), padding:'1px 6px', borderRadius:4 }}>
                        {lead.score || 0}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${STATUS_COLORS[lead.status] || 'badge-gray'}`}>
                      {lead.status}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-ghost" onClick={() => handleDelete(lead.id)} style={{ padding:'5px 7px' }}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > 20 && (
        <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:16, alignItems:'center' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p-1)} disabled={page === 1}>
            <ChevronLeft size={14} />
          </button>
          <span style={{ fontSize:13, color:'var(--muted)', padding:'0 8px' }}>
            Page {page} of {Math.ceil(total/20)}
          </span>
          <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p+1)} disabled={page === Math.ceil(total/20)}>
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {showAdd && (
        <AddLeadModal
          onClose={() => setShowAdd(false)}
          onSuccess={() => { setShowAdd(false); fetchLeads(); }}
        />
      )}
    </div>
  );
}

function AddLeadModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ name:'', email:'', company:'', role:'', campaign_id:'' });
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    api.get('/campaigns').then(res => setCampaigns(res.data.campaigns || []));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/leads', form);
      toast.success('Lead added!');
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div>
            <h2 style={{ fontSize:17, fontWeight:700 }}>Add Lead</h2>
            <p style={{ fontSize:12, color:'var(--muted)', marginTop:2 }}>Add a single contact manually</p>
          </div>
          <button className="btn btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {[
            { key:'name', label:'Full Name *', placeholder:'John Smith', required:true },
            { key:'email', label:'Email *', placeholder:'john@company.com', required:true, type:'email' },
            { key:'company', label:'Company', placeholder:'Acme Inc.' },
            { key:'role', label:'Role / Title', placeholder:'VP of Sales' },
          ].map(({ key, label, placeholder, required, type }) => (
            <div key={key}>
              <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:5, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.04em' }}>
                {label}
              </label>
              <input
                className="input"
                type={type||'text'}
                placeholder={placeholder}
                value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                required={required}
              />
            </div>
          ))}
          <div>
            <label style={{ display:'block', fontSize:12, fontWeight:600, marginBottom:5, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.04em' }}>
              Assign to Campaign
            </label>
            <select className="input" value={form.campaign_id} onChange={e => setForm(f => ({ ...f, campaign_id: e.target.value }))}>
              <option value="">— No campaign —</option>
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Adding...' : 'Add Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}